/** @type {Array<{ id?: number, code: string, name: string, manufacturer_id?: number|null, manufacturer_code?: string|null, manufacturer_name?: string|null, is_active?: boolean }>} */
let runtimeFurnizori = [];

/**
 * @param {unknown} rows
 */
export function setFurnizori(rows) {
    if (!Array.isArray(rows)) {
        runtimeFurnizori = [];
        return;
    }
    runtimeFurnizori = rows
        .map((row) => ({
            id: row?.id ?? null,
            code: String(row?.code ?? '').trim(),
            name: String(row?.name ?? '').trim(),
            manufacturer_id: row?.manufacturer_id ?? null,
            manufacturer_code: row?.manufacturer_code ?? null,
            manufacturer_name: row?.manufacturer_name ?? null,
            is_active: row?.is_active !== false,
        }))
        .filter((row) => row.code || row.name);
    runtimeFurnizori.sort((a, b) => String(a.code).localeCompare(String(b.code), undefined, { numeric: true }));
}

/**
 * @returns {Array<{ id?: number|null, code: string, name: string, title?: string }>}
 */
export function getFurnizori() {
    return runtimeFurnizori.map((row) => ({
        ...row,
        title: row.name || row.code,
    }));
}

/**
 * @param {unknown} row
 * @returns {{ id?: number|null, code: string, name: string, title?: string }|null}
 */
export function upsertFurnizor(row) {
    if (!row) {
        return null;
    }
    const normalized = {
        id: row?.id ?? null,
        code: String(row?.code ?? '').trim(),
        name: String(row?.name ?? '').trim(),
        manufacturer_id: row?.manufacturer_id ?? null,
        manufacturer_code: row?.manufacturer_code ?? null,
        manufacturer_name: row?.manufacturer_name ?? null,
        is_active: row?.is_active !== false,
    };
    if (!normalized.code && !normalized.name) {
        return null;
    }
    const existingIndex = runtimeFurnizori.findIndex((item) => (
        (normalized.id != null && item.id === normalized.id)
        || (normalized.code && item.code === normalized.code)
    ));
    if (existingIndex >= 0) {
        runtimeFurnizori.splice(existingIndex, 1, {
            ...runtimeFurnizori[existingIndex],
            ...normalized,
        });
    } else {
        runtimeFurnizori.push(normalized);
    }
    runtimeFurnizori.sort((a, b) => String(a.code).localeCompare(String(b.code), undefined, { numeric: true }));
    return {
        ...normalized,
        title: normalized.name || normalized.code,
    };
}

/**
 * Builds autocomplete options for furnizor selection by merging the furnizor
 * catalog with manufacturers (producătorii folosesc același cod de furnizor).
 *
 * @param {Array<{ id?: number|null, code?: string, name?: string, manufacturer_id?: number|null }>} furnizori
 * @param {Array<{ id?: number|null, code?: string, name?: string }>} manufacturers
 * @returns {Array<{ id?: number|null, code: string, name: string, title: string, manufacturer_id?: number|null, isManufacturer?: boolean }>}
 */
export function buildFurnizorSelectOptions(furnizori = [], manufacturers = []) {
    const byCode = new Map();
    const normalizeCode = (code) => String(code || '').trim().toLowerCase();

    const upsert = (code, entry) => {
        const key = normalizeCode(code);
        if (!key) {
            return;
        }
        const existing = byCode.get(key);
        if (!existing) {
            byCode.set(key, entry);
            return;
        }
        byCode.set(key, {
            ...existing,
            ...entry,
            id: entry.id ?? existing.id ?? null,
            manufacturer_id: entry.manufacturer_id ?? existing.manufacturer_id ?? null,
            isManufacturer: Boolean(entry.isManufacturer || existing.isManufacturer),
        });
    };

    (Array.isArray(furnizori) ? furnizori : []).forEach((row) => {
        const code = String(row?.code || '').trim();
        const name = String(row?.name || '').trim();
        if (!code && !name) {
            return;
        }
        upsert(code || name, {
            id: row?.id ?? null,
            code: code || name,
            name: name || code,
            manufacturer_id: row?.manufacturer_id ?? null,
            title: `${code || name} – ${name || code}`,
            isManufacturer: Boolean(row?.manufacturer_id),
        });
    });

    (Array.isArray(manufacturers) ? manufacturers : []).forEach((row) => {
        const code = String(row?.code || '').trim();
        const name = String(row?.name || '').trim();
        if (!code) {
            return;
        }
        // Do not set id: null here — that would wipe a real Furnizor id on merge
        // when the same code exists in both catalogs.
        upsert(code, {
            code,
            name: name || code,
            manufacturer_id: row?.id ?? null,
            title: `${code} – ${name || code}`,
            isManufacturer: true,
        });
    });

    return [...byCode.values()].sort((a, b) => (
        String(a.code).localeCompare(String(b.code), undefined, { numeric: true })
    ));
}

/**
 * @param {string|number} codeOrId
 * @returns {{ id?: number|null, code: string, name: string }|null}
 */
export function findFurnizor(codeOrId) {
    const needle = String(codeOrId ?? '').trim();
    if (!needle) {
        return null;
    }
    return getFurnizori().find((row) => (
        String(row.id) === needle || row.code === needle
    )) || null;
}

/**
 * Display labels for the Produse / bulk furnizor column filter.
 * Merges admin furnizor catalog names with manufacturer names so tabs that
 * use „producător ca furnizor” remain selectable (same catalogs as create/edit).
 *
 * @param {Array<{ name?: string, title?: string, code?: string }>} [furnizori]
 * @param {Array<{ name?: string, code?: string }>} [manufacturers]
 * @returns {Array<{ title: string, value: string }>}
 */
export function listFurnizorFilterOptions(furnizori = getFurnizori(), manufacturers = []) {
    const names = new Set();
    const add = (value) => {
        const label = String(value || '').trim();
        if (label) {
            names.add(label);
        }
    };

    (Array.isArray(furnizori) ? furnizori : []).forEach((row) => {
        add(row?.name || row?.title || row?.code);
    });
    (Array.isArray(manufacturers) ? manufacturers : []).forEach((row) => {
        add(row?.name || row?.code);
    });

    return Array.from(names)
        .sort((a, b) => a.localeCompare(b, 'ro'))
        .map((name) => ({
            title: name,
            value: name,
        }));
}
